import React, { useState, useMemo } from "react";
import {
  User,
  Activity,
  Award,
  Boxes,
  Package,
  ChevronRight,
  CheckCircle2,
  Timer,
  ClipboardList,
} from "lucide-react";

import { StaffActionDialog } from "./StaffActionDialog";
import { WarehouseTaskDetailDialog } from "../../pages/firm/warehouse/WarehouseTaskDetailDialog";
import { useWarehouseTaskStore } from "../../store/warehouseTask";

export const StaffPerformanceGrid = ({
  performanceData,
  logsData,
  loading,
}: any) => {
  const taskStore = useWarehouseTaskStore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogUser, setDialogUser] = useState<any>(null);
  const [dialogAction, setDialogAction] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);

  const handleOpenTaskDetail = async (taskId: number) => {
    const fullTask = await taskStore.getSingleTask(taskId, true);
    setSelectedTask(fullTask);
  };

  //------------------------------------------
  // 🔥 FILTER (sadece anlamlı personel)
  //------------------------------------------
  const filteredStaff = useMemo(() => {
    return (performanceData || []).filter((staff: any) => {
      return (
        (staff.totalTaskScore || 0) > 0 ||
        (staff.pickingDoneCount || 0) > 0 ||
        (staff.packagingDoneCount || 0) > 0 ||
        (staff.pickingOngoingCount || 0) > 0 ||
        (staff.packagingOngoingCount || 0) > 0 ||
        (staff.assignedPickingCount || 0) > 0 ||
        (staff.assignedPackagingCount || 0) > 0
      );
    });
  }, [performanceData]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 animate-pulse">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-40 card rounded-2xl" />
        ))}
      </div>
    );
  } 

  if(performanceData.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10">
        <User size={48} className="text-gray-400" />
        <div className="text-gray-500">Gösterilecek personel verisi yok</div>
      </div>
    );
  }
  const getInitials = (name: string) => {
    if (!name) return "P";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {filteredStaff.map((staff: any) => (
          <div
            key={staff.userId}
            className="card hover:border-primary/30 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all flex flex-col gap-4"
          >
            {/* HEADER */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                  {getInitials(staff.name)}
                </div>

                <div className="min-w-0">
                  <div className="font-bold text-[13px] truncate">
                    {staff.name}
                  </div>
                  <div className="text-[10px] opacity-50">Depo Personeli</div>
                </div>
              </div>

              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/10 text-amber-600 font-bold text-[11px]">
                <Award size={13} />
                {staff.totalTaskScore?.toFixed(1) || 0}
              </div>
            </div>

            {/* PICKING */}
            <button
              onClick={() => {
                setDialogUser(staff);
                setDialogAction("PICKING");
                setDialogOpen(true);
              }}
              className="p-3 rounded-xl bg-blue-500/5 hover:bg-blue-500/10 border border-transparent hover:border-blue-500/20"
            >
              <div className="flex items-center justify-between text-blue-600 font-bold text-xs uppercase">
                <div className="flex items-center gap-1">
                  <Boxes size={13} />
                  Toplama
                </div>
                <ChevronRight size={14} />
              </div>

              <div className="grid grid-cols-3 gap-2 mt-2 text-center text-[11px]">
                <div>
                  <div className="opacity-60">Atanan</div>
                  <div className="font-bold text-gray-700 dark:text-white">
                    {staff.assignedPickingCount || 0}
                  </div>
                </div>

                <div>
                  <div className="opacity-60">Biten</div>
                  <div className="font-bold text-green-500">
                    {staff.pickingDoneCount || 0}
                  </div>
                </div>

                <div>
                  <div className="opacity-60">Devam</div>
                  <div className="font-bold text-blue-500">
                    {staff.pickingOngoingCount || 0}
                  </div>
                </div>
              </div>
            </button>

            {/* PACKING */}
            <button
              onClick={() => {
                setDialogUser(staff);
                setDialogAction("PACKAGING");
                setDialogOpen(true);
              }}
              className="p-3 rounded-xl bg-green-500/5 hover:bg-green-500/10 border border-transparent hover:border-green-500/20"
            >
              <div className="flex items-center justify-between text-green-600 font-bold text-xs uppercase">
                <div className="flex items-center gap-1">
                  <Package size={13} />
                  Paketleme
                </div>
                <ChevronRight size={14} />
              </div>

              <div className="grid grid-cols-3 gap-2 mt-2 text-center text-[11px]">
                <div>
                  <div className="opacity-60">Atanan</div>
                  <div className="font-bold text-gray-700 dark:text-white">
                    {staff.assignedPackagingCount || 0}
                  </div>
                </div>

                <div>
                  <div className="opacity-60">Biten</div>
                  <div className="font-bold text-green-500">
                    {staff.packagingDoneCount || 0}
                  </div>
                </div>

                <div>
                  <div className="opacity-60">Devam</div>
                  <div className="font-bold text-green-600">
                    {staff.packagingOngoingCount || 0}
                  </div>
                </div>
              </div>
            </button>
          </div>
        ))}
      </div>

      <StaffActionDialog
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        user={dialogUser}
        actionType={dialogAction}
        logs={logsData}
        onOpenTaskDetail={handleOpenTaskDetail}
      />

      <WarehouseTaskDetailDialog
        isOpen={!!selectedTask}
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
      />
    </>
  );
};